/** ч.1105–1107: «Обліковий запис» → видалення і передача організації (лише власник акаунта). */
import { api } from './client';

export interface AccountOrg {
  id: string;
  name: string;
  code: string | null;
  /** ч.1406: акаунт, якому належить організація (перелік — по всіх власних акаунтах). */
  tenant_id?: string;
  tenant_name?: string;
  /** `EDO` — картка учасника без обліку. */
  plan?: 'BASE' | 'EDO' | null;
  entity_type?: 'LEGAL_ENTITY' | 'FOP' | 'INDIVIDUAL';
  /** організацію підтверджено ключем КЕП → незворотні дії лише з підписом цим ключем */
  kep_confirmed: boolean;
  deletion_mark: boolean;
  documents: number;
}

export interface DeletePreview {
  id: string;
  name: string;
  code: string | null;
  kep_required: boolean;
  rows: number;
  tables: number;
  files: number;
  highlights: { label: string; count: number }[];
  blockers: { table: string; column: string; references: string; rows: number }[];
}

export interface DestructivePayload {
  password: string;
  confirm_name: string;
  challenge?: string;
  signature_b64?: string;
}

export interface OrgRemoval {
  id: string;
  kind: 'DELETED' | 'TRANSFERRED' | 'RECEIVED';
  organization_name: string;
  organization_code: string | null;
  was_kep_confirmed: boolean;
  performed_by_name: string | null;
  counterparty: string | null;
  details: Record<string, unknown>;
  created_at: string;
}

export type KepAction = 'org-delete' | 'org-transfer' | 'account-delete';

/** ч.1406: дія над організацією іншого ВЛАСНОГО акаунта — з його акаунтом у заголовку. */
const inTenant = (tenantId?: string) => (tenantId ? { headers: { 'X-Tenant-Id': tenantId } } : {});

export const accountOrgs = {
  list: () => api.get<AccountOrg[]>('/account/organizations').then((r) => r.data),
  kepChallenge: (action: KepAction, subject: string, tenantId?: string) =>
    api.get<{ challenge: string }>('/account/kep-challenge', { params: { action, subject }, ...inTenant(tenantId) })
      .then((r) => r.data.challenge),
  deletePreview: (id: string, tenantId?: string) =>
    api.get<DeletePreview>(`/account/organizations/${id}/delete-preview`, inTenant(tenantId)).then((r) => r.data),
  remove: (id: string, p: DestructivePayload, tenantId?: string) =>
    api.post<DeletePreview>(`/account/organizations/${id}/delete`, p, inTenant(tenantId)).then((r) => r.data),
  removals: () => api.get<OrgRemoval[]>('/account/organization-removals').then((r) => r.data),
  // ч.1107: передача організації — сторона відправника
  transfer: (id: string, p: DestructivePayload & { to_email: string }) =>
    api.post<OrgTransfer>(`/account/organizations/${id}/transfer`, p).then((r) => r.data),
  outgoing: () => api.get<OrgTransfer[]>('/account/transfers').then((r) => r.data),
  cancelTransfer: (id: string) =>
    api.post<OrgTransfer>(`/account/transfers/${id}/cancel`).then((r) => r.data),
};

export interface OrgTransfer {
  id: string;
  organization_id: string;
  organization_name: string;
  organization_code: string | null;
  from_tenant_name: string | null;
  from_user_name: string | null;
  to_email: string;
  status: 'PENDING' | 'ACCEPTED' | 'DECLINED' | 'CANCELLED';
  created_at: string;
  expires_at: string;
  decided_at: string | null;
}

export interface TransferAccepted {
  organization_id: string;
  copied_rows: number;
  matched_rows: number;
}

/** ч.1107: сторона отримувача — не залежить від обраного акаунта. */
export const orgTransfers = {
  incoming: () => api.get<{ transfers: OrgTransfer[]; my_accounts: { id: string; name: string }[] }>(
    '/auth/org-transfers/incoming').then((r) => r.data),
  accept: (id: string, to_tenant_id: string, password: string) =>
    api.post<TransferAccepted>(`/auth/org-transfers/${id}/accept`, { to_tenant_id, password }).then((r) => r.data),
  decline: (id: string) => api.post(`/auth/org-transfers/${id}/decline`).then(() => undefined),
};

/** ч.1106: видалення облікового запису. */
export interface AccountDeletePreview {
  email: string;
  blocked_reason: string | null;
  tenants: {
    id: string; name: string; tenant_role: string; will_be_deleted: boolean; other_members: number;
    organizations: { id: string; name: string; code: string | null; kep_confirmed: boolean }[];
  }[];
}

export const accountSelf = {
  deletePreview: () => api.get<AccountDeletePreview>('/auth/account-delete/preview').then((r) => r.data),
  kepChallenge: (organization_id: string) =>
    api.get<{ challenge: string }>('/auth/account-delete/kep-challenge', { params: { organization_id } })
      .then((r) => r.data.challenge),
  remove: (p: { password: string; confirm_email: string;
    signatures: Record<string, { challenge: string; signature_b64: string }> }) =>
    api.post<{ tenants: unknown[]; files: number }>('/auth/account-delete', p).then((r) => r.data),
};
